import './community/ui/fonts.css';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';

import './index.css';

import { routerFor } from './App';
import { queryClient } from './hooks/queryClient';
import { TOOLTIP_DELAY_MS, TooltipProvider } from '@/components/ui/tooltip';
import { ensureSignedIn, safeReturnPath } from './auth/session';
import { authConfig, currentSession, probeSession, type Edition, type SessionUser } from './auth/passwordSession';
import { getConfig } from './config/runtimeConfig';
import { watchForSessionEnd } from './auth/sessionWatch';
import { guestReturnSignIn, isGuestPath } from './community/guestPaths';
import { prefetchStudyHead } from './community/api/studyHead';
import { DASHBOARD_PATH, isAdminPath } from './auth/adminRedirect';
import { startAnalytics } from './lib/analytics';

// NOTE: no React.StrictMode. dicom-microscopy-viewer (and OpenLayers) use a
// module-level web-worker manager that StrictMode's dev double-mount tears down
// out from under the live viewer. Teardown is handled in Viewport.tsx.
//
/** The path with the bundle's base prefix removed (VITE_BASE_PATH; '/' on every deployment today). */
function appPath(): string {
  const base = import.meta.env.BASE_URL.replace(/\/+$/, '');
  return base && window.location.pathname.startsWith(base) ? window.location.pathname.slice(base.length) : window.location.pathname;
}

/**
 * Is this a page a GUEST may open in the community edition? See
 * community/guestPaths.ts. Share links (`/s/…`) are among them: a link holder
 * may have no account, and the gate below would otherwise show them a sign-in
 * form. Enterprise has no share links, so there they are gated like anything.
 */
function isGuestSurface(): boolean {
  return isGuestPath(appPath());
}

/**
 * /dev/* is the community shell's fixture mode (dev builds only): the
 * component kit and the pages over an in-memory fake API. It needs no
 * account, so it skips the gate the way a share link does and always mounts
 * the community shell. See community/dev/fixtures.ts.
 */
const kit = import.meta.env.DEV && /\/dev(\/|$)/.test(window.location.pathname);

/** `me` is the session boot found, for the community shell to start from;
 *  undefined when boot did not settle it (the enterprise gate). */
async function boot(): Promise<{ edition: Edition; guest: boolean; me?: SessionUser | null }> {
  // Administration lives in the operator dashboard; send /admin there before
  // any session or edition request, whoever is asking (it signs people in).
  if (isAdminPath(appPath())) {
    window.location.replace(DASHBOARD_PATH);
    return new Promise<never>(() => {});
  }
  if (kit) return { edition: 'community', guest: false };
  // The session is asked for alongside the edition rather than after it: one
  // round trip less before anything draws (~85 ms on prod). Only where the
  // session is this server's cookie (password mode, both editions); under an
  // identity provider the gate has its own way in. See SessionProbe for what a
  // null answer means.
  const password = getConfig().auth.mode === 'password';
  const early = password ? probeSession() : Promise.resolve(null);
  // A study page's head goes with them, for the same reason (studyHead.ts).
  if (password) prefetchStudyHead(appPath(), queryClient);
  const edition = await serverEdition();
  if (edition === 'community') {
    // The community shell has its own sign-in screens (community/auth) and
    // never mounts the enterprise gate. A guest page renders for anybody; a
    // gated page sends a stranger to /signin and brings them back after.
    const asked = await early;
    const me = asked ? asked.me : await currentSession().catch(() => null);
    if (me) {
      // Somebody else on this origin (the dashboard) sent us here to refresh
      // the session and wants the person back. Same parsing rule as the
      // enterprise gate: on this origin or not at all.
      const back = safeReturnPath(new URLSearchParams(window.location.search).get('return'));
      if (back && back !== window.location.pathname + window.location.search) {
        window.location.replace(back);
        return new Promise<never>(() => {});
      }
    }
    if (!me) {
      // `/` is open to guests, but not when someone was sent here to sign in
      // and come back (the dashboard's 401 bounce): that visit is a sign-in.
      const back = guestReturnSignIn(
        appPath(),
        safeReturnPath(new URLSearchParams(window.location.search).get('return')),
      );
      if (back) {
        const base = import.meta.env.BASE_URL.replace(/\/+$/, '');
        window.location.replace(`${base}/signin?return=${encodeURIComponent(back)}`);
        return new Promise<never>(() => {});
      }
    }
    if (me || isGuestSurface()) return { edition, guest: !me, me };
    const base = import.meta.env.BASE_URL.replace(/\/+$/, '');
    const here = window.location.pathname + window.location.search;
    window.location.replace(`${base}/signin?return=${encodeURIComponent(here)}`);
    return new Promise<never>(() => {});
  }
  // Auth gate: with no auth config (local mode) this resolves immediately; in
  // cloud it completes the login flow (or redirects to it) before first render.
  await ensureSignedIn(early);
  return { edition, guest: false };
}

/**
 * The edition, from the server (the shell is the server's call; see App.tsx).
 *
 * If the server cannot be reached, WAIT for it rather than guess. The old
 * fallback guessed "enterprise", so whenever the API was down a community
 * visitor got the enterprise sign-in form instead of the community one, and
 * there appeared to be two login screens. Nothing renders until the server
 * answers, which is also the safe direction: no shell mounts for anybody
 * before the server has said which one this deployment is.
 */
async function serverEdition(): Promise<Edition> {
  for (let attempt = 0; ; attempt++) {
    try {
      const { edition } = await authConfig();
      clearUnreachable();
      return edition;
    } catch {
      showUnreachable();
      await new Promise((r) => setTimeout(r, Math.min(10_000, 1000 * 2 ** Math.min(attempt, 4))));
    }
  }
}

/** A plain notice in #root while the server is unreachable; no shell, no form. */
function showUnreachable(): void {
  const root = document.getElementById('root');
  if (!root || document.getElementById('tr-unreachable')) return;
  const box = document.createElement('div');
  box.id = 'tr-unreachable';
  box.setAttribute('role', 'status');
  box.style.cssText = 'margin:auto;max-width:360px;padding:24px;text-align:center;font:14px/1.5 system-ui,sans-serif;color:inherit';
  const title = document.createElement('div');
  title.style.cssText = 'font-size:15px;font-weight:600;margin-bottom:6px';
  title.textContent = 'Can\u2019t reach the server';
  const body = document.createElement('div');
  body.style.cssText = 'opacity:.7';
  body.textContent = 'Trying again in a moment. This page will continue on its own.';
  box.append(title, body);
  root.replaceChildren(box);
}

function clearUnreachable(): void {
  document.getElementById('tr-unreachable')?.remove();
}

boot().then(({ edition, guest, me }) => {
  const router = routerFor(edition);
  // The community shell reads the session (and the account preferences riding
  // on it) as ['session']; start it from boot's answer instead of asking again
  // the moment the page draws.
  if (me !== undefined) queryClient.setQueryData(['session'], me);
  // AFTER the gate, never before. Watching for 401s is what tells the app its
  // session has ended — but /auth/login answers 401 for a wrong password, and
  // arming this first would drape "your session has ended" over the sign-in
  // form of somebody who has not signed in yet.
  //
  // The gate itself only runs once, before this render; without this nothing
  // ever checks again, and an expiry an hour later showed up as an empty
  // worklist and an upload page reporting failures.
  // Not for a guest (share link or community Explore): it watches for 401s to
  // detect an ended session, and a guest never had one — arming it would drape
  // "your session has ended" over somebody who was never signed in.
  if (!guest && !kit) watchForSessionEnd();

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={TOOLTIP_DELAY_MS}>
        <RouterProvider router={router} />
      </TooltipProvider>
    </QueryClientProvider>,
  );
  if (!kit) startAnalytics(edition, me);
});
